import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore, TuneOutlined } from '@mui/icons-material';

/**
 * One label/value row of the review summary.
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param {React.ReactNode} props.children - Value.
 * @param {function(): void} [props.onEdit] - Jump back to change it.
 * @returns {JSX.Element} Row.
 */
function SummaryRow({ label, children, onEdit }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr auto', sm: '140px 1fr auto' },
        alignItems: 'center',
        gap: { xs: 0.25, sm: 1.5 },
        py: 1.25,
        borderBottom: '1px solid var(--q-row-divider)',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ gridColumn: { xs: '1 / 2', sm: 'auto' } }}>{label}</Typography>
      <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / 2', sm: 'auto' }, gridRow: { xs: 2, sm: 'auto' }, fontSize: 13.5, overflowWrap: 'anywhere' }}>
        {children}
      </Box>
      {onEdit ? (
        <Button size="small" onClick={onEdit} sx={{ gridRow: { xs: '1 / 3', sm: 'auto' } }} aria-label={`Change ${label.toLowerCase()}`}>
          Change
        </Button>
      ) : <span />}
    </Box>
  );
}

/**
 * Step 3: review the selections, name the document, set options and generate.
 * @param {object} props - Props.
 * @param {object} props.template - Selected template.
 * @param {object|undefined} props.quality - Template quality (document templates).
 * @param {object} props.project - Selected project.
 * @param {string} props.evidenceLabel - Human summary of the chosen evidence.
 * @param {number} props.fileCount - Supporting files (always included by retrieval).
 * @param {string} props.documentName - Document name.
 * @param {function(string): void} props.onDocumentNameChange - Rename.
 * @param {string} props.tone - Selected tone id.
 * @param {function(string): void} props.onToneChange - Change tone.
 * @param {Array<{title: string, frontendTitle: string, explanation: string}>} props.tones - Tones.
 * @param {string} props.error - Generation error.
 * @param {function(number): void} props.onEditStep - Jump to a step (0 template, 1 sources).
 * @returns {JSX.Element} Step body.
 */
export default function ReviewStep({
  template,
  quality,
  project,
  evidenceLabel,
  fileCount,
  documentName,
  onDocumentNameChange,
  tone,
  onToneChange,
  tones,
  error,
  onEditStep,
}) {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [toneHelpOpen, setToneHelpOpen] = useState(false);
  const selectedTone = tones.find((t) => t.title === tone);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}

      <TextField
        fullWidth
        label="Document name"
        value={documentName}
        onChange={(e) => onDocumentNameChange(e.target.value)}
        error={!documentName.trim()}
        helperText={documentName.trim() ? 'Used for the document and its exports.' : 'Give the document a name.'}
        inputProps={{ maxLength: 200 }}
      />

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-xl)', px: 2, bgcolor: 'background.paper' }}>
        <SummaryRow label="Template" onEdit={() => onEditStep(0)}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
            <span>{template?.name}</span>
            {quality && <Chip size="small" color={quality.color} label={`${quality.metrics.promptCount} AI fields · ${quality.label}`} />}
          </Box>
        </SummaryRow>
        <SummaryRow label="Project" onEdit={() => onEditStep(1)}>
          {project?.name}
          {project?.customer?.name && (
            <Typography component="span" variant="body2" color="text.secondary"> · {project.customer.name}</Typography>
          )}
        </SummaryRow>
        <SummaryRow label="Evidence" onEdit={() => onEditStep(1)}>{evidenceLabel}</SummaryRow>
        {fileCount > 0 && (
          <SummaryRow label="Supporting files">
            {fileCount} uploaded file{fileCount === 1 ? '' : 's'}, used where relevant
          </SummaryRow>
        )}
        <SummaryRow label="Tone">{selectedTone?.frontendTitle || tone}</SummaryRow>
      </Box>

      <Box>
        <Button
          color="inherit"
          startIcon={<TuneOutlined />}
          endIcon={<ExpandMore sx={{ transform: optionsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />}
          onClick={() => setOptionsOpen((v) => !v)}
          aria-expanded={optionsOpen}
          aria-controls="generator-options"
        >
          Options
        </Button>
        <Collapse in={optionsOpen}>
          <Box id="generator-options" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
            <FormControl fullWidth>
              <InputLabel id="tone-select-label">Writing tone</InputLabel>
              <Select
                labelId="tone-select-label"
                value={tone}
                label="Writing tone"
                onChange={(e) => onToneChange(e.target.value)}
              >
                {tones.map((t) => (
                  <MenuItem key={t.title} value={t.title}>{t.frontendTitle}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedTone?.explanation && (
              <Typography variant="body2" color="text.secondary">{selectedTone.explanation}</Typography>
            )}
            <Box>
              <Button size="small" onClick={() => setToneHelpOpen((v) => !v)} aria-expanded={toneHelpOpen}>
                {toneHelpOpen ? 'Hide' : 'Compare'} all tones
              </Button>
              <Collapse in={toneHelpOpen}>
                <List dense disablePadding>
                  {tones.map((t) => (
                    <ListItem key={t.title} disableGutters>
                      <ListItemText
                        primary={t.frontendTitle}
                        secondary={t.explanation}
                        primaryTypographyProps={{ sx: { fontSize: 13.5, fontWeight: 600 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          </Box>
        </Collapse>
      </Box>

      <Typography variant="caption" color="text.secondary">
        Generation is repeatable: the same template and evidence give the same document. Fixed template text is copied as written.
        After it finishes you can regenerate for a different take.
      </Typography>
    </Box>
  );
}
